"use client";

/**
 * Friendly full-screen overlay shown on a brand's PUBLIC storefront (shop,
 * product detail, checkout, etc.) when that brand's subscription has lapsed or
 * its trial is over. It deliberately reveals NOTHING about billing/subscriptions
 * to shoppers — it simply, warmly, says the store isn't taking orders right now,
 * so customers don't place orders the brand can't fulfil. POS pages are excluded
 * (they have their own paywall).
 */

import { Clock } from "lucide-react";

export function StorefrontUnavailable() {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Store currently unavailable"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-5"
      style={{
        background:
          "radial-gradient(120% 120% at 50% 0%, rgba(30,41,59,0.55), rgba(15,23,42,0.75))",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-rose-400 to-violet-500" />
        <div className="px-8 py-10 text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-50 to-rose-50 ring-1 ring-amber-100">
            <Clock className="h-9 w-9 text-amber-500" strokeWidth={1.75} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
            We&rsquo;ll be right back
          </h1>
          <p className="mx-auto mt-3 max-w-xs text-[15px] leading-relaxed text-gray-500">
            This store isn&rsquo;t taking orders at the moment. Please check back
            again soon — thank you for your patience.
          </p>
          <div className="mt-7 inline-flex items-center gap-2 rounded-full bg-gray-50 px-4 py-2 text-xs font-medium text-gray-400 ring-1 ring-gray-100">
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            Temporarily unavailable
          </div>
          {/* Discreet owner escape — a lapsed brand's owner can still sign in to
              pay. Neutral wording so it reveals nothing to shoppers. /signin is
              excluded from this overlay (see BrandLayoutClient), so it loads. */}
          <div className="mt-8 border-t border-gray-100 pt-5">
            <a
              href="/signin"
              className="text-[13px] font-medium text-gray-400 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-600"
            >
              Business owner? Sign in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StorefrontUnavailable;
